import { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  useTheme,
  MenuItem,
} from "@mui/material";
import axios from "axios";
import SimpleTable from "../../components/SimpleTable";
import { tokens } from "../../theme";
import StockoutTable from "../../components/StockoutTable";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
dayjs.extend(utc);

const StockoutReport = () => {
  const theme = useTheme();
   const today = dayjs().format("YYYY-MM-DD");
  const colors = tokens(theme.palette.mode);

  const [stockouts, setStockouts] = useState([]);
  const [summary, setSummary] = useState({});
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [itemId, setItemId] = useState("");
  const [warehouseId, setWarehouseId] = useState("");
  const [userId, setUserId] = useState("");

  const [items, setItems] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);

  // Fetch dropdowns
  const fetchItems = useCallback(async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/item`);
      setItems(res.data || []);
    } catch (err) {
      console.error("Error fetching items:", err);
    }
  }, []);

  const fetchWarehouses = useCallback(async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/warehouse`
      );
      setWarehouses(res.data || []);
    } catch (err) {
      console.error("Error fetching warehouses:", err);
    }
  }, []);

  const fetchUsers = useCallback(async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/user`);
      const allUsers = res.data || [];

      const filtered = allUsers.filter((u) =>
        u.Role?.name?.toLowerCase().includes("sale")
      );
      setUsers(filtered);
    } catch (err) {
      console.error("Error fetching users:", err);
    }
  }, []);

  useEffect(() => {
    fetchItems();
    fetchWarehouses();
    fetchUsers();
  }, [fetchItems, fetchWarehouses, fetchUsers]);

  // Fetch report
  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const payload = {};
      if (startDate) payload.startDate = startDate;
      if (endDate) payload.endDate = endDate;
      if (itemId) payload.itemId = itemId;
      if (warehouseId) payload.warehouseId = warehouseId;
      if (userId) payload.userId = userId;

      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/stockout/report`,
        payload
      );

      setStockouts(res.data.report || []);
      setSummary(res.data.summary || {});
    } catch (err) {
      console.error(err);
      setStockouts([]);
      setSummary({});
    }
    setLoading(false);
  }, [startDate, endDate, itemId, warehouseId, userId]);

  useEffect(() => {
  fetchReport();
}, [fetchReport]);

  const columns = [
    {
      field: "createdAt",
      headerName: "Stock Out Date",
      width: 150,
      valueFormatter: ({ value }) => dayjs.utc(value).format("YYYY-MM-DD"),
    },
    { field: "user", headerName: "Sales", width: 180 },
    { field: "item", headerName: "Item", width: 180 },
    { field: "amount", headerName: "Quantity", width: 120 },
    { field: "warehouse", headerName: "Warehouse", width: 180 },
    { field: "car", headerName: "Car", width: 120 },
    { field: "status", headerName: "Status", width: 120 },
  ];

  const gridSx = {
    "& .MuiDataGrid-root": { border: "none" },
    "& .MuiDataGrid-cell": { borderBottom: "none" },
    "& .MuiDataGrid-columnHeaders": {
      backgroundColor: colors.blueAccent[700],
      borderBottom: "none",
    },
    "& .MuiDataGrid-virtualScroller": {
      backgroundColor: colors.primary[400],
    },
    "& .MuiDataGrid-footerContainer": {
      borderTop: "none",
      backgroundColor: colors.blueAccent[700],
    },
    "& .MuiCheckbox-root": {
      color: `${colors.greenAccent[200]} !important`,
    },
    "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
      color: `${colors.grey[100]} !important`,
    },
  };

  return (
    <Box p={3}>
      <Typography variant="h4" mb={2}>
        Stockout Report
      </Typography>

      <Box display="flex" flexWrap="wrap" gap={2} mb={2} alignItems="center">
        <TextField
          label="Start Date"
          type="date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label="End Date"
          type="date"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          select
          label="Item"
          value={itemId}
          onChange={(e) => setItemId(e.target.value)}
          sx={{ minWidth: 160 }}
        >
          <MenuItem value="">All</MenuItem>
          {items.map((i) => (
            <MenuItem key={i.id} value={i.id}>
              {i.name}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label="Warehouse"
          value={warehouseId}
          onChange={(e) => setWarehouseId(e.target.value)}
          sx={{ minWidth: 160 }}
        >
          <MenuItem value="">All</MenuItem>
          {warehouses.map((w) => (
            <MenuItem key={w.id} value={w.id}>
              {w.name}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label="Sales User"
          value={userId}
          onChange={(e) => setUserId(e.target.value)}
          sx={{ minWidth: 160 }}
        >
          <MenuItem value="">All</MenuItem>
          {users.map((u) => (
            <MenuItem key={u.id} value={u.id}>
              {u.fullName || u.userName}
            </MenuItem>
          ))}
        </TextField>
        <Button variant="contained" onClick={fetchReport} disabled={loading}>
          {loading ? "Loading..." : "Generate Report"}
        </Button>
      </Box>

      <Box mb={2}>
        <Typography>Total Stockouts: {summary.totalStockouts || 0}</Typography>
        <Typography>Total Quantity: {summary.totalQuantity || 0}</Typography>
      </Box>

      <StockoutTable
        rows={stockouts}
        summary={summary}
        themeColors={tokens(theme.palette.mode)}
      />
    </Box>
  );
};

export default StockoutReport;
